import { useMemo } from 'react';
import RedirectLink from '@Components/RedirectLink';
import TableTemplate from '@Dashboard/Charts/TableTemplate';
import { MAIL_SERVICES } from '@Dashboard/RiskUser/utils';
import { generateRedirectUrlToLog } from '@Log/shared';
import { useDashboardServicesStore } from '@Models/useDashboardServicesModel';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { ServiceNameEnum } from '@Types/index';
import { LogTypeEnum, SecurityFilterName } from '@Types/log';
import { getServiceNamesByValues } from '@Utils/service';
import { getUtcTime } from '@Utils/time';
import { useColorMode, useColorStyle, Box, Text } from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import ContextMenu from '@Components/ContextMenu';

const SpamGraymailRecipientsTable = (props: {
  insightsData: any;
  day: string;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const timeModel = useUtcOffsetStore();
  const servicesModel = useDashboardServicesStore();
  const services = servicesModel.enabledServices;

  const config = [
    { width: '20%', grow: 2, align: 'left', tooltip: true },
    { width: '15%', grow: 1, align: 'right' },
    { width: '15%', grow: 1, align: 'right' },
    { width: '15%', grow: 1, align: 'right' },
    { width: '20%', grow: 2, align: 'left' },
  ];
  const header = [
    t('riskUser.topUser.user'),
    t('riskUser.topUser.totalAmount'),
    t('riskUser.topUser.graymail'),
    t('riskUser.topUser.otherSpam'),
    t('riskUser.topUser.received'),
  ];

  const tableData = useMemo(() => {
    const getTotal = (x: any) => {
      return (x.spam_graymail || 0) + (x.spam_other || 0);
    };

    const data = props.insightsData?.SuspiciousTopRecipients?.[props.day] || [];
    data.sort((a: any, b: any) => {
      return getTotal(b) - getTotal(a);
    });

    return data.map((x: any) => {
      return [
        x.key || '',
        renderSpamLink('all', getTotal(x), x.key || '', props.day, services),
        renderSpamLink(
          'graymail',
          x.spam_graymail || 0,
          x.key || '',
          props.day,
          services
        ),
        renderSpamLink(
          'otherspam',
          x.spam_other || 0,
          x.key || '',
          props.day,
          services
        ),
        x.last_detected
          ? getUtcTime(
              x.last_detected,
              timeModel.offset,
              t('common.momentDateFormat')
            )
          : '',
      ];
    });
  }, [props.insightsData, props.day, services, timeModel.offset, t]);

  return (
    <Box>
      <Box
        mb="24px"
        fontSize="18px"
        fontWeight="600"
        lineHeight="24px"
        color={colorStyle.color.primary}
      >
        {t('riskUser.topUser.spamRecipient')}
      </Box>
      <TableTemplate
        height="220px"
        config={config}
        header={header}
        data={tableData}
        appendContextMenu={{
          columnIndex: 0,
          enabled: true,
          component: (user: string) => {
            return (
              <ContextMenu user={user} responseActionShow={false}></ContextMenu>
            );
          },
        }}
      />
    </Box>
  );
};

function renderSpamLink(
  type: 'all' | 'graymail' | 'otherspam',
  count: number,
  recipient: string,
  day: string,
  services: ServiceNameEnum[]
) {
  if (count === 0) {
    return 0;
  }
  return (
    <RedirectLink
      href={`logs?${generateRedirectUrlToLog(
        [
          {
            field: 'spamCategory',
            values: [
              type === 'all' || type === 'graymail' ? 'GrayEmail Bulk' : '',
              type === 'all' || type === 'graymail' ? 'GrayEmail Forum' : '',
              type === 'all' || type === 'graymail'
                ? 'GrayEmail Social Network'
                : '',
              type === 'all' || type === 'graymail' ? 'GrayEmail MML' : '',
              type === 'all' || type === 'otherspam' ? 'Spam Others' : '',
            ].filter((x) => !!x),
            type: 'terms',
          },
          {
            field: 'violator',
            values: [recipient],
            type: 'terms',
          },
          // {
          //   field: 'mailDirection',
          //   type: 'mustNot',
          //   values: ['out'],
          //   subType: 'terms',
          // },
          {
            field: 'securityFilterName',
            values: [SecurityFilterName.AdvancedSpamProtection],
            type: 'terms',
          },
          {
            field: 'scanSource',
            values: getServiceNamesByValues(
              services.filter((x) => MAIL_SERVICES.has(x))
            ),
            type: 'terms',
          },
        ],
        day,
        LogTypeEnum.DETECTION
      )}`}
    >
      <Text>{count}</Text>
    </RedirectLink>
  );
}

export default SpamGraymailRecipientsTable;
